import { useEffect, useRef, useState } from "react";
import StakeholderCombobox from "@app/components/stakeholder-combobox";
import AccountCombobox from "@app/components/account-combobox";
import type { AccountOption } from "@app/components/account-combobox";
import FilterEditor from "./filter-editor";
import { DeltaPreview } from "./recapture-dialog";
import type { Delta } from "./recapture-dialog";
import { MATCH_KINDS } from "@app/lib/match-kinds";

// Everything the dialog needs about the rule being edited (a structural subset
// of the rules-list Rule, so the dialog isn't coupled to the generated type).
export type RuleForEdit = {
  readonly id: string;
  readonly regex: string;
  readonly matchKind: string;
  readonly direction: string;
  readonly caseSensitive: boolean;
  readonly targetType: string;
  readonly targetClassification: string | null | undefined;
  readonly stakeholder: { readonly id: string; readonly name: string } | null | undefined;
  readonly destinationBankAccount:
    | {
        readonly id: string;
        readonly name: string;
        readonly nickname: string | null | undefined;
        readonly last4: string | null | undefined;
      }
    | null
    | undefined;
  readonly expenseNature: { readonly id: string; readonly name: string } | null | undefined;
  readonly startDate: string | null | undefined;
  readonly endDate: string | null | undefined;
  readonly accounts: ReadonlyArray<{ readonly id: string }>;
};

// The match-set-affecting fields — the parent runs ruleDeltaPreview with these.
export type PreviewVars = {
  regex: string;
  matchKind: string;
  direction: string;
  caseSensitive: boolean;
  startDate: string | null;
  endDate: string | null;
  accountIds: string[];
};

const DIRECTIONS = ["ANY", "OUTFLOW", "INFLOW"];

const CLASSIFICATIONS = [
  "CHARGEBACK",
  "DIVIDEND",
  "FRAUDULENT",
  "LOAN_PROCEEDS",
  "LOAN_REPAYMENT",
  "SALARY",
];

type Draft = {
  regex: string;
  matchKind: string;
  caseSensitive: boolean;
  direction: string;
  // target (one flavor per rule)
  stakeholderId: string | null;
  stakeholderName: string | null; // set when creating a counterparty on the fly
  stakeholderLabel: string; // display only
  targetClassification: string | null;
  destinationBankAccountId: string | null;
  destinationLabel: string; // display only
  expenseNatureId: string | null;
  expenseNatureLabel: string; // display only
  // filters
  startDate: string | null;
  endDate: string | null;
  accountIds: string[];
};

function accountLabel(a: {
  name: string;
  nickname?: string | null;
  last4?: string | null;
}): string {
  return `${a.nickname || a.name}${a.last4 ? ` ••${a.last4}` : ""}`;
}

function initialDraft(rule: RuleForEdit): Draft {
  return {
    regex: rule.regex,
    matchKind: rule.matchKind,
    caseSensitive: rule.caseSensitive,
    direction: rule.direction,
    stakeholderId: rule.stakeholder?.id ?? null,
    stakeholderName: null,
    stakeholderLabel: rule.stakeholder?.name ?? "—",
    targetClassification: rule.targetClassification ?? null,
    destinationBankAccountId: rule.destinationBankAccount?.id ?? null,
    destinationLabel: rule.destinationBankAccount
      ? accountLabel(rule.destinationBankAccount)
      : "—",
    expenseNatureId: rule.expenseNature?.id ?? null,
    expenseNatureLabel: rule.expenseNature?.name ?? "—",
    startDate: rule.startDate ?? null,
    endDate: rule.endDate ?? null,
    accountIds: rule.accounts.map((a) => a.id),
  };
}

function sameSet(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

// Rich, single-dialog rule editor. Edits the pattern + match kind, case
// sensitivity, direction, target, and date/account filters all at once, and
// previews the match-set delta (which txns ENTER and LEAVE the rule) live as the
// draft changes. The parent owns the ruleDeltaPreview fetch (debounced here) and
// the updateRule mutation; the dialog hands back a ready UpdateRule input.
export default function EditRuleDialog({
  rule,
  stakeholders,
  accounts,
  natures,
  delta,
  previewing,
  saving,
  onPreview,
  onSave,
  onCancel,
}: {
  rule: RuleForEdit;
  stakeholders: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  accounts: ReadonlyArray<AccountOption>;
  natures: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  delta: Delta | null;
  previewing: boolean;
  saving: boolean;
  onPreview: (vars: PreviewVars) => void;
  onSave: (input: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => initialDraft(rule));
  const [overrideManual, setOverrideManual] = useState(false);
  const [pickingStakeholder, setPickingStakeholder] = useState(false);
  const [pickingDestination, setPickingDestination] = useState(false);

  const isRegex = draft.matchKind === "REGEX";
  const patchedDraft = (next: Partial<Draft>) => setDraft((d) => ({ ...d, ...next }));

  // Debounced live preview. Only the match-set fields matter — changing the
  // target re-points matches but never alters which txns match, so it doesn't
  // re-fetch. matchKey serializes exactly those fields so the effect is stable.
  const matchKey = JSON.stringify([
    draft.regex,
    draft.matchKind,
    draft.direction,
    draft.caseSensitive,
    draft.startDate,
    draft.endDate,
    draft.accountIds,
  ]);
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;
  useEffect(() => {
    const vars: PreviewVars = {
      regex: draft.regex,
      matchKind: draft.matchKind,
      direction: draft.direction,
      caseSensitive: draft.caseSensitive,
      startDate: draft.startDate,
      endDate: draft.endDate,
      accountIds: draft.accountIds,
    };
    const timer = setTimeout(() => onPreviewRef.current(vars), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchKey]);

  const save = () => {
    const input: Record<string, unknown> = { ruleId: rule.id };
    if (draft.regex !== rule.regex) input.regex = draft.regex;
    if (draft.matchKind !== rule.matchKind) input.matchKind = draft.matchKind;
    if (draft.caseSensitive !== rule.caseSensitive)
      input.caseSensitive = draft.caseSensitive;
    if (draft.direction !== rule.direction) input.direction = draft.direction;

    if (rule.targetType === "STAKEHOLDER") {
      if (draft.stakeholderName) input.stakeholderName = draft.stakeholderName;
      else if (draft.stakeholderId && draft.stakeholderId !== rule.stakeholder?.id)
        input.stakeholderId = draft.stakeholderId;
    } else if (rule.targetType === "CLASSIFICATION") {
      if (
        draft.targetClassification &&
        draft.targetClassification !== rule.targetClassification
      )
        input.targetClassification = draft.targetClassification;
    } else if (rule.targetType === "INTERNAL_LINK") {
      if (
        draft.destinationBankAccountId &&
        draft.destinationBankAccountId !== rule.destinationBankAccount?.id
      )
        input.destinationBankAccountId = draft.destinationBankAccountId;
    } else if (rule.targetType === "NATURE") {
      if (
        draft.expenseNatureId &&
        draft.expenseNatureId !== rule.expenseNature?.id
      )
        input.expenseNatureId = draft.expenseNatureId;
    }

    if ((draft.startDate ?? null) !== (rule.startDate ?? null))
      input.startDate = draft.startDate;
    if ((draft.endDate ?? null) !== (rule.endDate ?? null))
      input.endDate = draft.endDate;
    if (!sameSet(draft.accountIds, rule.accounts.map((a) => a.id)))
      input.accountIds = draft.accountIds;

    if (overrideManual && delta && delta.droppedManual.length > 0)
      input.overrideTxnIds = delta.droppedManual.map((r) => r.txn.id);

    onSave(input);
  };

  const patternUnchanged =
    draft.regex.trim() === "" ||
    JSON.stringify(initialDraft(rule)) === JSON.stringify(draft);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[680px] max-h-[88vh] overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold text-gray-800">Edit rule</h2>

        {/* ---- Pattern + match kind ---- */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <select
              value={draft.matchKind}
              onChange={(e) => patchedDraft({ matchKind: e.target.value })}
              className="rounded border px-2 py-1.5 text-sm bg-white shrink-0"
            >
              {MATCH_KINDS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <input
              value={draft.regex}
              onChange={(e) => patchedDraft({ regex: e.target.value })}
              placeholder={isRegex ? "^ACME.*PAYMENT$" : "ACME"}
              className="flex-1 rounded border px-2 py-1.5 font-mono text-xs"
            />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-gray-400">
              {MATCH_KINDS.find((m) => m.value === draft.matchKind)?.hint}
            </p>
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={draft.caseSensitive}
                onChange={(e) => patchedDraft({ caseSensitive: e.target.checked })}
              />
              Case sensitive
            </label>
          </div>
        </div>

        {/* ---- Target + direction ---- */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-xs text-gray-500 uppercase">
              {rule.targetType === "INTERNAL_LINK"
                ? "Internal link"
                : rule.targetType === "CLASSIFICATION"
                ? "Classification"
                : rule.targetType === "NATURE"
                ? "Expense nature"
                : "Counterparty"}
            </label>
            {rule.targetType === "CLASSIFICATION" ? (
              <select
                value={draft.targetClassification ?? ""}
                onChange={(e) => patchedDraft({ targetClassification: e.target.value })}
                className="w-full rounded border px-2 py-1.5 text-sm bg-white"
              >
                {CLASSIFICATIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            ) : rule.targetType === "INTERNAL_LINK" ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPickingDestination((v) => !v)}
                  className="w-full text-left rounded border px-2 py-1.5 text-sm hover:bg-gray-50"
                >
                  {draft.destinationLabel}
                </button>
                {pickingDestination && (
                  <div className="absolute z-20 mt-1 bg-white rounded border shadow-lg p-2">
                    <AccountCombobox
                      options={accounts}
                      allowCreate={false}
                      onPick={(choice) => {
                        setPickingDestination(false);
                        if (choice.bankAccountId) {
                          const a = accounts.find((x) => x.id === choice.bankAccountId);
                          patchedDraft({
                            destinationBankAccountId: choice.bankAccountId,
                            destinationLabel: a ? accountLabel(a) : draft.destinationLabel,
                          });
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            ) : rule.targetType === "NATURE" ? (
              <select
                value={draft.expenseNatureId ?? ""}
                onChange={(e) => {
                  const n = natures.find((x) => x.id === e.target.value);
                  patchedDraft({
                    expenseNatureId: e.target.value || null,
                    expenseNatureLabel: n?.name ?? "—",
                  });
                }}
                className="w-full rounded border px-2 py-1.5 text-sm bg-white"
              >
                <option value="" disabled>
                  Select a nature…
                </option>
                {natures.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPickingStakeholder((v) => !v)}
                  className="w-full text-left rounded border px-2 py-1.5 text-sm hover:bg-gray-50"
                >
                  {draft.stakeholderName ?? draft.stakeholderLabel}
                </button>
                {pickingStakeholder && (
                  <div className="absolute z-20 mt-1 bg-white rounded border shadow-lg p-2">
                    <StakeholderCombobox
                      options={stakeholders}
                      onPick={(choice) => {
                        setPickingStakeholder(false);
                        if (choice.stakeholderId) {
                          const s = stakeholders.find(
                            (x) => x.id === choice.stakeholderId
                          );
                          patchedDraft({
                            stakeholderId: choice.stakeholderId,
                            stakeholderName: null,
                            stakeholderLabel: s?.name ?? draft.stakeholderLabel,
                          });
                        } else if (choice.name) {
                          patchedDraft({
                            stakeholderName: choice.name,
                            stakeholderId: null,
                            stakeholderLabel: choice.name,
                          });
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-500 uppercase">Direction</label>
            <select
              value={draft.direction}
              onChange={(e) => patchedDraft({ direction: e.target.value })}
              className="w-full rounded border px-2 py-1.5 text-sm bg-white"
            >
              {DIRECTIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ---- Filters ---- */}
        <div className="border-t pt-3">
          <p className="text-xs text-gray-500 uppercase mb-2">Filters</p>
          <FilterEditor
            draft={{
              startDate: draft.startDate,
              endDate: draft.endDate,
              accountIds: draft.accountIds,
            }}
            accounts={accounts}
            onChange={(f) =>
              patchedDraft({
                startDate: f.startDate,
                endDate: f.endDate,
                accountIds: [...f.accountIds],
              })
            }
          />
        </div>

        {/* ---- Live impact preview ---- */}
        <div className="border-t pt-3">
          <p className="text-xs text-gray-500 uppercase mb-2">Impact</p>
          <DeltaPreview
            delta={delta}
            loading={previewing}
            overrideManual={overrideManual}
            onToggleOverride={setOverrideManual}
          />
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            disabled={saving || previewing || patternUnchanged}
            onClick={save}
            className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
            title={
              patternUnchanged ? "Change a field to apply" : "Apply this rule change"
            }
          >
            {saving ? "Applying…" : "Apply changes"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
